import { useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import styles from "./EnablePushPrompt.module.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

/**
 * Convite para ativar as notificacoes, mostrado com o app JA instalado.
 *
 * Existe porque o navegador so abre o dialogo de permissao a partir de um
 * toque do usuario (o Safari/iOS descarta o pedido automatico). Antes, esse
 * toque dependia de um sino pequeno no cabecalho que quase ninguem notava:
 * muita gente instalava o app e nunca ativava os avisos. O botao daqui chama
 * o mesmo handler do sino, entao o pedido continua partindo de um gesto.
 *
 * Foco, Tab e Escape vem do ModalHeader (useFocusTrap): nao ha listener de
 * teclado aqui, senao o Escape dispararia onDismiss duas vezes.
 *
 * Props:
 *   open       - bool
 *   onEnable   - () => Promise - dispara o pedido de permissao
 *   onDismiss  - () => void - "Agora nao" ou fechar
 */
export default function EnablePushPrompt({ open, onEnable, onDismiss }) {
  const [busy, setBusy] = useState(false);
  useBodyScrollLock(open);

  if (!open) return null;

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onEnable?.();
    } finally {
      setBusy(false);
    }
  };
  // Enquanto o pedido esta em andamento, fechar nao e dispensar: o dialogo do
  // sistema esta na frente e o resultado ja e registrado por quem pediu.
  const handleDismiss = () => { if (!busy) onDismiss?.(); };

  return createPortal(
    <div className={styles.backdrop} onClick={handleDismiss} role="presentation">
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Ativar avisos do treino"
      >
        <ModalHeader onClose={handleDismiss} />

        <div className={styles.body}>
          <div className={styles.hero}>
            <span className={styles.heroIcon}>
              <FontAwesomeIcon icon={faBell} />
            </span>
            <div>
              <h2 className={styles.title}>Ative os avisos do treino</h2>
              <p className={styles.subtitle}>O app já está instalado. Falta só um toque.</p>
            </div>
          </div>

          <div className={styles.benefits}>
            <ul>
              <li>Lembrete do treino do dia e da sua meta da semana</li>
              <li>Aviso quando seu professor responder ou mandar algo novo</li>
            </ul>
          </div>

          <p className={styles.footnote}>
            Ao tocar em <strong>Ativar avisos</strong>, o celular vai pedir sua autorização.
            Toque em <strong>Permitir</strong>. Você pode desligar quando quiser.
          </p>
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.btn} onClick={handleDismiss} disabled={busy}>
            Agora não
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleEnable}
            disabled={busy}
          >
            <FontAwesomeIcon icon={faBell} /> {busy ? "Ativando..." : "Ativar avisos"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
